export function bindDialog(backdrop: HTMLElement, onClose: () => void): () => void {
  const previousFocus = document.activeElement as HTMLElement | null;
  const focusable = () =>
    Array.from(
      backdrop.querySelectorAll<HTMLElement>(
        'button, input, select, textarea, a[href], [tabindex="0"]',
      ),
    ).filter((element) => !element.hasAttribute('disabled') && element.getClientRects().length);
  const onKey = (event: KeyboardEvent) => {
    if (!backdrop.isConnected) return;
    const dialogs = document.querySelectorAll('.modal-backdrop');
    if (dialogs.length && dialogs[dialogs.length - 1] !== backdrop) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    }
    if (event.key === 'Tab') {
      const items = focusable();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0],
        last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };
  document.addEventListener('keydown', onKey, true);
  const initial = focusable();
  (initial.find((element) => element.matches('input, select, textarea')) ?? initial[0])?.focus();
  return () => {
    document.removeEventListener('keydown', onKey, true);
    if (previousFocus?.isConnected) previousFocus.focus();
  };
}

export function askText(label: string, initial = ''): Promise<string | null> {
  return appQuestion(label, initial) as Promise<string | null>;
}

export async function askConfirm(message: string): Promise<boolean> {
  return Boolean(await appQuestion(message));
}

function appQuestion(message: string, initial?: string): Promise<string | boolean | null> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const card = document.createElement('section');
    card.className = 'setup-modal question-modal';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    const title = document.createElement('h2');
    title.id = `question-${crypto.randomUUID()}`;
    title.textContent = initial === undefined ? 'Confirm action' : message;
    card.setAttribute('aria-labelledby', title.id);
    const heading = document.createElement('div');
    heading.className = 'modal-head';
    heading.append(title);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'mini-btn';
    close.textContent = '×';
    close.setAttribute('aria-label', 'Close dialog');
    heading.append(close);
    const form = document.createElement('form');
    let input: HTMLInputElement | HTMLTextAreaElement | undefined;
    if (initial !== undefined) {
      input = /JSON/i.test(message)
        ? document.createElement('textarea')
        : document.createElement('input');
      input.value = initial;
      input.setAttribute('aria-label', message);
      input.maxLength = /JSON/i.test(message) ? 100000 : 500;
      if (input instanceof HTMLTextAreaElement) input.rows = 8;
      form.append(input);
    } else {
      const copy = document.createElement('p');
      copy.className = 'modal-copy';
      copy.textContent = message;
      form.append(copy);
    }
    const error = document.createElement('p');
    error.className = 'cloud-error';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    form.append(error);
    const actions = document.createElement('div');
    actions.className = 'modal-actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'ghost-btn';
    cancel.textContent = 'Cancel';
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'primary-btn';
    submit.textContent = initial === undefined ? 'Confirm' : 'Save';
    actions.append(cancel, submit);
    form.append(actions);
    card.append(heading, form);
    backdrop.append(card);
    document.body.append(backdrop);
    const finish = (value: string | boolean | null) => {
      release();
      backdrop.remove();
      resolve(value);
    };
    const release = bindDialog(backdrop, () => finish(null));
    close.onclick = cancel.onclick = () => finish(null);
    form.onsubmit = (event) => {
      event.preventDefault();
      if (input && !input.value.trim()) {
        error.textContent = 'Enter a value to continue.';
        error.hidden = false;
        input.focus();
        return;
      }
      finish(input ? input.value : true);
    };
  });
}
